import { formatMin, MS_PER_MINUTE, type ReminderPlan, type ReminderSlot } from '@wiztime/core';
import type { TFunction } from 'i18next';
import type { NoticeButton, ReminderNotice } from './notifier';
import { ReminderAction } from './reminderProtocol';

const itemsText = (slots: readonly ReminderSlot[], t: TFunction): string =>
  slots
    // The date as well as the time: on a transition day one item has two occurrences (plan §4).
    .map((slot) => t('reminder.item', { name: slot.name, time: `${slot.occursAt.slice(5, 10)} ${formatMin(slot.plannedMin)}` }))
    .join(t('reminder.itemSeparator'));

/** "Done" only for a single item (ticking several at once could tick one not done); "later" always. */
const buttonsFor = (slots: readonly ReminderSlot[], t: TFunction): NoticeButton[] => [
  ...(slots.length === 1 ? [{ action: ReminderAction.Done, title: t('reminder.actionDone') }] : []),
  { action: ReminderAction.Snooze, title: t('reminder.actionSnooze') },
];

/**
 * The notifications of a plan: one per planned time ("in 5 minutes" while the planned time is
 * ahead, "time to check in" once it is reached), plus one summary of the missed ones.
 */
export function noticesOf(plan: ReminderPlan, now: number, t: TFunction): ReminderNotice[] {
  const onTime = plan.onTime.map((group): ReminderNotice => {
    const first = group[0]!;
    const minutesLeft = Math.ceil((first.plannedAt - now) / MS_PER_MINUTE);
    return {
      title: minutesLeft >= 1 ? t('reminder.titleSoon', { count: minutesLeft }) : t('reminder.titleNow'),
      body: itemsText(group, t),
      tagSuffix: String(first.plannedAt),
      keys: group.map((slot) => slot.key),
      buttons: buttonsFor(group, t),
    };
  });
  if (plan.missed.length === 0) return onTime;
  const missed: ReminderNotice = {
    title: t('reminder.titleMissed', { count: plan.missed.length }),
    body: itemsText(plan.missed, t),
    tagSuffix: `missed-${plan.missed[0]!.personalDate}`,
    keys: plan.missed.map((slot) => slot.key),
    buttons: buttonsFor(plan.missed, t),
  };
  return [...onTime, missed];
}
